import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { useT } from '../i18n';
import SignupPlanPicker, { SignupPlanSummary } from './SignupPlanPicker';
import SignalGlobe from './SignalGlobe';
import {
  adoptPlanFromUrl,
  readPendingPlan,
  savePendingPlan,
  signupRequestedInUrl,
} from '../lib/billingFlow';
import { fetchInstanceLicence } from '../lib/instanceLicence';
import { ArrowRight, Eye, EyeOff, Gauge, Infinity as InfinityIcon, Mail, ShieldCheck } from 'lucide-react';
import { BACKEND_URL } from '../runtimeConfig';
import { LetsCampaignMark } from './LetsCampaignLogo';

// Wave 15 — env-resolved API base, mirrors App.js. We cannot import from
// App.js because of the circular ``useAuth`` dep, so duplicate the trim.
const API = `${BACKEND_URL}/api`;
const SITE = 'https://letscampaign.com';

function Logo() {
  return (
    <span className="inline-flex items-center gap-2.5 text-foreground">
      <LetsCampaignMark className="h-9 w-9 shrink-0" />
      <span className="font-display text-xl font-bold tracking-tight">LetsCampaign</span>
    </span>
  );
}

function Field({ label, hint, trailing, ...rest }) {
  const id = rest.name || label.toLowerCase().replace(/\s+/g, '-');
  return (
    <label className="block" htmlFor={id}>
      <span className="mb-2 block text-sm font-medium text-foreground">{label}</span>
      <span className="relative block">
        <input
          {...rest}
          id={id}
          className={
            'w-full rounded-xl border border-input bg-background px-4 py-3 text-sm text-foreground ' +
            'placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-ring ' +
            (trailing ? 'pe-12' : '')
          }
        />
        {trailing ? <span className="absolute inset-y-0 end-0 flex items-center pe-3">{trailing}</span> : null}
      </span>
      {hint ? <span className="mt-2 block text-xs text-muted-foreground">{hint}</span> : null}
    </label>
  );
}

function Submit({ children, disabled, loading }) {
  return (
    <button
      type="submit"
      disabled={disabled || loading}
      className={
        'inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3.5 text-sm ' +
        'font-semibold text-primary-foreground transition-all hover:brightness-110 active:scale-[0.99] ' +
        'disabled:pointer-events-none disabled:opacity-55'
      }
    >
      {children}
      {loading ? null : <ArrowRight className="h-4 w-4" />}
    </button>
  );
}

function Alert({ children }) {
  if (!children) return null;
  return (
    <div role="alert" className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
      {children}
    </div>
  );
}

function Aside({ t }) {
  const points = [
    { Icon: InfinityIcon, title: t('Clicks are never counted'), text: t('However much traffic you send, the invoice does not move.') },
    { Icon: ShieldCheck, title: t('Bots filtered on arrival'), text: t('Datacenter, VPN and proxy traffic is handled before a page loads.') },
    { Icon: Gauge, title: t('First link in five minutes'), text: t('Add a source, create a campaign, copy the link.') },
  ];
  return (
    <div className="w-full max-w-md">
      <p className="font-mono text-[0.625rem] uppercase tracking-[0.18em] text-muted-foreground">
        {t('Your workspace')}
      </p>
      <h2 className="mt-4 font-display text-2xl font-semibold text-foreground">
        {t('Everything is already running')}
      </h2>
      <ul className="mt-8 flex flex-col gap-6">
        {points.map(({ Icon, title, text }) => (
          <li key={title} className="flex gap-4">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10">
              <Icon className="h-4 w-4 text-primary" />
            </span>
            <span>
              <span className="block text-sm font-semibold text-foreground">{title}</span>
              <span className="mt-1 block text-sm leading-relaxed text-muted-foreground">{text}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function AuthForm() {
  const { login, verify2fa, register } = useAuth();
  const { t } = useT();
  const [showPassword, setShowPassword] = useState(false);
  // A link from the website (?plan=pro&interval=annual or ?signup=1) opens sign-up.
  const [activeTab, setActiveTab] = useState(() => (signupRequestedInUrl() ? 'register' : 'login'));

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ email: '', password: '', fullName: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Registration is only possible with a plan (see SignupPlanPicker).
  const [signupPlan, setSignupPlan] = useState(() => adoptPlanFromUrl() || readPendingPlan());
  // A self-hosted install has no plans to choose: its licence covers it.
  const [selfHosted, setSelfHosted] = useState(false);
  useEffect(() => {
    let alive = true;
    fetchInstanceLicence(API).then((d) => {
      if (alive) setSelfHosted(Boolean(d && d.mode === 'self_hosted'));
    });
    return () => {
      alive = false;
    };
  }, []);
  const [pendingVerifyEmail, setPendingVerifyEmail] = useState(null);
  const [resendState, setResendState] = useState('idle');
  const [twoFa, setTwoFa] = useState({ challenge: null, code: '' });
  const [ssoProviders, setSsoProviders] = useState([]);
  const [registrationEnabled, setRegistrationEnabled] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!API.startsWith('/')) {
      axios.get(`${API}/auth/sso/providers`).then(
        (r) => {
          if (!cancelled) {
            const list = (r.data && r.data.providers) || [];
            setSsoProviders(list.map((p) => p.provider).filter(Boolean));
          }
        },
        () => {
          if (!cancelled) setSsoProviders([]);
        },
      );
      axios.get(`${API}/auth/registration-status`).then(
        (r) => {
          if (cancelled) return;
          const enabled = r.data && r.data.enabled !== false;
          setRegistrationEnabled(enabled);
          if (!enabled) setActiveTab('login');
        },
        () => {},
      );
    }
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const hash = (typeof window !== 'undefined' && window.location.hash) || '';
    if (!hash || !hash.includes('sso_error=')) return;
    const params = new URLSearchParams(hash.replace(/^#/, ''));
    const code = params.get('sso_error');
    const desc = params.get('sso_error_description');
    if (code) {
      setError(desc ? `${t('SSO failed')}: ${desc}` : t('SSO failed'));
      try {
        window.history.replaceState(null, '', window.location.pathname);
      } catch (_e) {
        /* no-op */
      }
    }
  }, []);

  const handleSsoStart = (provider) => {
    if (!provider) return;
    setError('');
    setLoading(true);
    axios.get(`${API}/auth/sso/${provider}/login`).then(
      (r) => {
        const url = r.data && r.data.authorization_url;
        if (url) {
          window.location.assign(url);
        } else {
          setError(t('SSO is not configured'));
          setLoading(false);
        }
      },
      (err) => {
        setError(err.response?.data?.detail || t('SSO is not configured'));
        setLoading(false);
      },
    );
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(loginData.email, loginData.password);
    if (result?.requires_2fa) {
      setTwoFa({ challenge: result.challenge_token, code: '' });
    } else if (result?.email_not_verified) {
      setResendState('idle');
      setPendingVerifyEmail(result.email || loginData.email);
    } else if (!result?.ok) {
      setError(result?.detail || t('Invalid email or password'));
    }
    setLoading(false);
  };

  const handleTwoFa = async (e) => {
    e.preventDefault();
    if (!twoFa.challenge || !twoFa.code) return;
    setError('');
    setLoading(true);
    const ok = await verify2fa(twoFa.challenge, twoFa.code.trim());
    if (!ok) {
      setError(t('Invalid 2FA code'));
    } else {
      setTwoFa({ challenge: null, code: '' });
    }
    setLoading(false);
  };

  const choosePlan = (choice) => {
    savePendingPlan(choice.plan, choice.interval);
    setSignupPlan(choice);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    // After signup BillingGate sends the account straight to Stripe Checkout.
    if (signupPlan && !selfHosted) savePendingPlan(signupPlan.plan, signupPlan.interval);
    const success = await register(registerData.email, registerData.password, registerData.fullName);
    if (success?.verification_required) {
      setResendState('idle');
      setPendingVerifyEmail(success.email || registerData.email);
    } else if (!success) {
      setError(t('Registration failed. Please try again.'));
    }
    setLoading(false);
  };

  const handleResendVerification = async () => {
    if (!pendingVerifyEmail || resendState === 'busy') return;
    setResendState('busy');
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: pendingVerifyEmail });
      setResendState('sent');
    } catch (_e) {
      setResendState('idle');
      setError(t('Could not resend the email — try again.'));
    }
  };

  const isLoginInvalid = !loginData.email.trim() || !loginData.password;
  const isRegisterInvalid =
    !registerData.email.trim() ||
    registerData.password.length < 8 ||
    registerData.password.length > 128 ||
    !registerData.fullName.trim();

  const eyeButton = (
    <button
      type="button"
      aria-label={showPassword ? t('Hide password') : t('Show password')}
      className="text-muted-foreground transition-colors hover:text-foreground"
      onClick={() => setShowPassword((v) => !v)}
    >
      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
    </button>
  );

  let heading = t('Sign in');
  let lead = t('Your workspace, your campaigns, your numbers.');
  if (pendingVerifyEmail) {
    heading = t('Confirm your email');
    lead = t('Open the link we sent and your workspace is ready.');
  } else if (twoFa.challenge) {
    heading = t('Enter your code');
    lead = t('Six digits from your authenticator app.');
  } else if (activeTab === 'register') {
    heading = t('Create your account');
    lead = t('Two fields and a password. Tracking is live in minutes.');
  }

  return (
    <main className="relative min-h-screen bg-background">
      <div className="relative mx-auto grid min-h-screen w-full max-w-[1500px] lg:grid-cols-2">
        <div className="flex flex-col px-6 py-10 sm:px-12 lg:px-16 xl:px-24">
          <a href={SITE} className="inline-flex w-fit">
            <Logo />
          </a>

          <div className="flex flex-1 flex-col justify-center py-12">
            <div className="w-full max-w-md">
              <h1 className="font-display text-3xl font-semibold text-foreground sm:text-4xl">{heading}</h1>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{lead}</p>

              <div className="mt-8 flex flex-col gap-5">
                <Alert>{error}</Alert>

                {pendingVerifyEmail ? (
                  <div className="flex flex-col gap-5">
                    <div className="flex items-start gap-3 rounded-xl border border-border bg-secondary/40 px-4 py-4">
                      <Mail className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <p className="text-sm leading-relaxed text-foreground">
                        {t('We sent a confirmation link to')} <b>{pendingVerifyEmail}</b>.{' '}
                        {t('Check spam if it is not there within a minute. The link lasts 24 hours.')}
                      </p>
                    </div>
                    {resendState === 'sent' ? (
                      <p className="text-sm text-foreground">{t('Sent again. Give it a moment.')}</p>
                    ) : null}
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      disabled={resendState === 'busy'}
                      className="inline-flex w-full items-center justify-center rounded-xl border border-border bg-background px-6 py-3.5 text-sm font-semibold text-foreground transition-colors hover:bg-accent disabled:opacity-55"
                    >
                      {resendState === 'busy' ? t('Sending') : t('Resend the email')}
                    </button>
                    <button
                      type="button"
                      className="text-xs text-muted-foreground hover:text-foreground"
                      onClick={() => {
                        setPendingVerifyEmail(null);
                        setError('');
                      }}
                    >
                      {t('Use a different account')}
                    </button>
                  </div>
                ) : twoFa.challenge ? (
                  <form onSubmit={handleTwoFa} className="flex flex-col gap-5">
                    <Field
                      label={t('Verification code')}
                      name="code"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder="000000"
                      value={twoFa.code}
                      onChange={(e) => setTwoFa((s) => ({ ...s, code: e.target.value }))}
                      required
                    />
                    <Submit loading={loading} disabled={!twoFa.code}>
                      {loading ? t('Checking') : t('Continue')}
                    </Submit>
                    <button
                      type="button"
                      className="text-xs text-muted-foreground hover:text-foreground"
                      onClick={() => setTwoFa({ challenge: null, code: '' })}
                    >
                      {t('Use a different account')}
                    </button>
                  </form>
                ) : activeTab === 'login' ? (
                  <form onSubmit={handleLogin} className="flex flex-col gap-5">
                    <Field
                      label={t('Email')}
                      name="email"
                      type="email"
                      autoComplete="email"
                      placeholder="you@company.com"
                      value={loginData.email}
                      onChange={(e) => setLoginData((s) => ({ ...s, email: e.target.value }))}
                      required
                    />
                    <Field
                      label={t('Password')}
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      value={loginData.password}
                      onChange={(e) => setLoginData((s) => ({ ...s, password: e.target.value }))}
                      trailing={eyeButton}
                      required
                    />
                    <Submit loading={loading} disabled={isLoginInvalid}>
                      {loading ? t('Signing in') : t('Sign in')}
                    </Submit>
                  </form>
                ) : !signupPlan && !selfHosted ? (
                  <SignupPlanPicker onContinue={choosePlan} />
                ) : (
                  <form onSubmit={handleRegister} className="flex flex-col gap-5">
                    {!selfHosted && (
                      <SignupPlanSummary plan={signupPlan} onChange={() => setSignupPlan(null)} />
                    )}
                    <Field
                      label={t('Your name')}
                      name="fullName"
                      autoComplete="name"
                      value={registerData.fullName}
                      onChange={(e) => setRegisterData((s) => ({ ...s, fullName: e.target.value }))}
                      required
                    />
                    <Field
                      label={t('Work email')}
                      name="register-email"
                      type="email"
                      autoComplete="email"
                      placeholder="you@company.com"
                      value={registerData.email}
                      onChange={(e) => setRegisterData((s) => ({ ...s, email: e.target.value }))}
                      required
                    />
                    <Field
                      label={t('Password')}
                      name="register-password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      hint={t('At least 8 characters.')}
                      value={registerData.password}
                      onChange={(e) => setRegisterData((s) => ({ ...s, password: e.target.value }))}
                      trailing={eyeButton}
                      required
                    />
                    <Submit loading={loading} disabled={isRegisterInvalid}>
                      {loading ? t('Creating') : t('Create account')}
                    </Submit>
                  </form>
                )}

                {!pendingVerifyEmail && !twoFa.challenge && ssoProviders.length > 0 ? (
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                      <span className="h-px flex-1 bg-border" />
                      <span className="text-xs text-muted-foreground">{t('or')}</span>
                      <span className="h-px flex-1 bg-border" />
                    </div>
                    {ssoProviders.map((provider) => (
                      <button
                        key={provider}
                        type="button"
                        onClick={() => handleSsoStart(provider)}
                        className="inline-flex w-full items-center justify-center rounded-xl border border-border bg-background px-6 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-accent"
                      >
                        {t('Continue with')} {provider}
                      </button>
                    ))}
                  </div>
                ) : null}

                {!pendingVerifyEmail && !twoFa.challenge ? (
                  <p className="text-sm text-muted-foreground">
                    {activeTab === 'login' ? (
                      registrationEnabled ? (
                        <>
                          {t('No account yet?')}{' '}
                          <button
                            type="button"
                            className="font-medium text-primary hover:underline"
                            onClick={() => {
                              setActiveTab('register');
                              setError('');
                            }}
                          >
                            {t('Create one')}
                          </button>
                        </>
                      ) : (
                        t('New accounts are closed at the moment.')
                      )
                    ) : (
                      <>
                        {t('Already have an account?')}{' '}
                        <button
                          type="button"
                          className="font-medium text-primary hover:underline"
                          onClick={() => {
                            setActiveTab('login');
                            setError('');
                          }}
                        >
                          {t('Sign in')}
                        </button>
                      </>
                    )}
                  </p>
                ) : null}
              </div>
            </div>
          </div>

          <div className="text-xs text-muted-foreground">
            <a className="hover:text-foreground" href={`${SITE}/en/legal/terms`}>
              {t('Terms')}
            </a>
            <span className="px-2">{t('and')}</span>
            <a className="hover:text-foreground" href={`${SITE}/en/legal/privacy`}>
              {t('Privacy')}
            </a>
          </div>
        </div>

        <div className="relative hidden items-center overflow-hidden border-s border-border bg-secondary/25 px-16 lg:flex">
          <SignalGlobe className="auth-globe-side" />
          <div className="relative z-10">
            <Aside t={t} />
          </div>
        </div>
      </div>
    </main>
  );
}
